import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRinging } from "@phosphor-icons/react";
import api, { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DAYS, TIMES, fmtTime12, overdueText } from "./useRecurring";

export function OverdueSettingsTab({ current, onSaved }) {
  const [f, setF] = useState(current);
  const [busy, setBusy] = useState(false);
  useEffect(() => setF(current), [current]);
  const dirty = JSON.stringify(f) !== JSON.stringify(current);
  const save = async () => {
    setBusy(true);
    try { await api.put("/service/recurring/overdue-settings", f); toast.success("Overdue notice schedule saved"); onSaved(); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not save")); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4 p-1" data-testid="overdue-settings-tab">
      <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-3">
        <BellRinging size={18} className="text-rose-600 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-800">Weekly overdue notices</div>
          <div className="text-xs text-slate-600">While a recurring service is overdue, its contacts get one notice per week until it is marked completed or moved. Applies to every recurring service.</div>
        </div>
        <Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="overdue-settings-enabled" />
      </div>
      <div className={`grid grid-cols-2 gap-3 ${f.enabled ? "" : "opacity-50 pointer-events-none"}`}>
        <div>
          <Label>Day of the week</Label>
          <Select value={String(f.weekday)} onValueChange={(v) => setF({ ...f, weekday: Number(v) })}>
            <SelectTrigger data-testid="overdue-settings-day"><SelectValue /></SelectTrigger>
            <SelectContent>{DAYS.map((d, i) => <SelectItem key={d} value={String(i)} data-testid={`overdue-day-${i}`}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Time (Eastern)</Label>
          <Select value={f.time} onValueChange={(v) => setF({ ...f, time: v })}>
            <SelectTrigger data-testid="overdue-settings-time"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">{TIMES.map((t) => <SelectItem key={t} value={t} data-testid={`overdue-time-${t}`}>{fmtTime12(t)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-slate-500" data-testid="overdue-settings-summary">{overdueText(f)}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setF({ enabled: true, weekday: 1, time: "09:00" })} data-testid="overdue-settings-reset">Reset to Tuesday 9 AM</Button>
          <Button size="sm" disabled={!dirty || busy} onClick={save} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="overdue-settings-save">Save</Button>
        </div>
      </div>
    </div>
  );
}
